import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import CircularProgress from '@mui/material/CircularProgress'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import Database from '../../../types/Database'
import AcknowledgementHistory from '../../../components/AcknowledgementHistory'
import { useTestDataStore } from '../../../stores/testDataStore'

type AccountStatus = Database.AccountStatus

interface UserRow {
  id: string
  displayName: string
  email: string
  accountStatus: Database.AccountStatus
  accreditationStatus: string
  platformFeeOverride: number | null
  createdAt: string
}

const STATUS_COLORS: Record<AccountStatus, 'success' | 'warning' | 'error'> = {
  active: 'success',
  suspended: 'warning',
  banned: 'error'
}

const UserManagementTab = () => {
  const showTestData = useTestDataStore(s => s.showTestData)
  const [users, setUsers] = useState<UserRow[]>([])
  const [filteredUsers, setFilteredUsers] = useState<UserRow[]>([])
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState<AccountStatus | 'all'>('all')
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

  // Fee editing state: userId -> draft value (string for input control)
  const [feeEdits, setFeeEdits] = useState<Record<string, string>>({})

  // Feedback state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    try {
      const db = getFirestore()
      const usersRef = collection(db, 'users')
      const q = query(usersRef, where('testData', '==', showTestData))
      const snapshot = await getDocs(q)
      const userList: UserRow[] = snapshot.docs.map(d => {
        const data = d.data()
        return {
          id: d.id,
          displayName: data.displayName ?? 'Unknown',
          email: data.email ?? '',
          accountStatus:
            (data.accountStatus as Database.AccountStatus) ?? 'active',
          accreditationStatus: data.accreditation?.status ?? 'None',
          platformFeeOverride: data.platformFeeOverride ?? null,
          createdAt: data.createdAt?.toDate?.()
            ? data.createdAt.toDate().toLocaleDateString()
            : '—'
        }
      })
      setUsers(userList)
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to load users: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error'
      })
    } finally {
      setLoading(false)
    }
  }, [showTestData])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers, showTestData])

  // Client-side filtering
  useEffect(() => {
    let result = users

    if (statusFilter !== 'all') {
      result = result.filter(u => u.accountStatus === statusFilter)
    }

    if (searchText.trim()) {
      const lower = searchText.toLowerCase()
      result = result.filter(
        u =>
          u.displayName.toLowerCase().includes(lower) ||
          u.email.toLowerCase().includes(lower)
      )
    }

    setFilteredUsers(result)
  }, [users, searchText, statusFilter])

  const callAdminFunction = async (
    functionName: string,
    userId: string,
    actionLabel: string
  ) => {
    setActionLoading(userId)
    try {
      const functions = getFunctions()
      const fn = httpsCallable<{ userId: string }, { success: boolean }>(
        functions,
        functionName
      )
      await fn({ userId })

      setSnackbar({
        open: true,
        message: `User ${actionLabel} successfully.`,
        severity: 'success'
      })
      await fetchUsers()
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to ${actionLabel.toLowerCase()} user: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error'
      })
    } finally {
      setActionLoading(null)
    }
  }

  const handleSuspend = (userId: string) =>
    callAdminFunction('suspendUser', userId, 'suspended')

  const handleBan = (userId: string) =>
    callAdminFunction('banUser', userId, 'banned')

  const handleUnsuspend = (userId: string) =>
    callAdminFunction('unsuspendUser', userId, 'unsuspended')

  const handleFeeChange = (userId: string, value: string) => {
    setFeeEdits(prev => ({ ...prev, [userId]: value }))
  }

  const handleFeeSave = async (userId: string) => {
    const raw = feeEdits[userId]
    // Empty string or undefined means clear the override
    const isClearing = raw === undefined || raw.trim() === ''
    const parsedPercent = isClearing ? null : parseFloat(raw)

    if (!isClearing) {
      if (isNaN(parsedPercent!) || parsedPercent! < 0 || parsedPercent! > 100) {
        setSnackbar({
          open: true,
          message: 'Fee must be a number between 0 and 100 (percent).',
          severity: 'error'
        })
        return
      }
    }

    // Convert percent to decimal for the backend
    const feeRate = parsedPercent !== null ? parsedPercent / 100 : null

    setActionLoading(userId)
    try {
      const functions = getFunctions()
      const fn = httpsCallable<
        { userId: string; platformFeeOverride: number | null },
        { success: boolean }
      >(functions, 'setGuidePlatformFee')
      await fn({ userId, platformFeeOverride: feeRate })

      setSnackbar({
        open: true,
        message:
          feeRate !== null
            ? `Platform fee set to ${parsedPercent}% for this guide.`
            : 'Platform fee override cleared (default 5% will apply).',
        severity: 'success'
      })
      setFeeEdits(prev => {
        const next = { ...prev }
        delete next[userId]
        return next
      })
      await fetchUsers()
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to update fee: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error'
      })
    } finally {
      setActionLoading(null)
    }
  }

  const handleFeeClear = async (userId: string) => {
    setFeeEdits(prev => ({ ...prev, [userId]: '' }))
    setActionLoading(userId)
    try {
      const functions = getFunctions()
      const fn = httpsCallable<
        { userId: string; platformFeeOverride: number | null },
        { success: boolean }
      >(functions, 'setGuidePlatformFee')
      await fn({ userId, platformFeeOverride: null })

      setSnackbar({
        open: true,
        message: 'Platform fee override cleared (default 5% will apply).',
        severity: 'success'
      })
      setFeeEdits(prev => {
        const next = { ...prev }
        delete next[userId]
        return next
      })
      await fetchUsers()
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to clear fee: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error'
      })
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' py={6}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant='h6' sx={{ mb: 2 }}>
        User Management
      </Typography>

      {/* Search and Filter Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label='Search by name or email'
          variant='outlined'
          size='small'
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          sx={{ minWidth: 250 }}
        />
        <FormControl size='small' sx={{ minWidth: 150 }}>
          <InputLabel id='account-status-filter-label'>Status</InputLabel>
          <Select
            labelId='account-status-filter-label'
            value={statusFilter}
            label='Status'
            onChange={e =>
              setStatusFilter(e.target.value as AccountStatus | 'all')
            }
          >
            <MenuItem value='all'>All</MenuItem>
            <MenuItem value='active'>Active</MenuItem>
            <MenuItem value='suspended'>Suspended</MenuItem>
            <MenuItem value='banned'>Banned</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Display Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Account Status</TableCell>
              <TableCell>Accreditation</TableCell>
              <TableCell>Platform Fee %</TableCell>
              <TableCell>Registered</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align='center'>
                  <Typography color='text.secondary'>
                    No users found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map(user => {
                const currentFeeDisplay =
                  user.platformFeeOverride !== null
                    ? (user.platformFeeOverride * 100).toString()
                    : ''
                const editValue =
                  feeEdits[user.id] !== undefined
                    ? feeEdits[user.id]
                    : currentFeeDisplay

                return (
                  <TableRow
                    key={user.id}
                    hover
                    onClick={() =>
                      setSelectedUserId(prev =>
                        prev === user.id ? null : user.id
                      )
                    }
                    selected={selectedUserId === user.id}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{user.displayName}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.accountStatus}
                        color={STATUS_COLORS[user.accountStatus] ?? 'default'}
                        size='small'
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.accreditationStatus}
                        size='small'
                        variant='outlined'
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <TextField
                          size='small'
                          type='number'
                          placeholder='5'
                          value={editValue}
                          onChange={e =>
                            handleFeeChange(user.id, e.target.value)
                          }
                          disabled={actionLoading === user.id}
                          inputProps={{ min: 0, max: 100, step: 0.5 }}
                          sx={{ width: 70 }}
                        />
                        <Tooltip title='Save fee override'>
                          <span>
                            <IconButton
                              size='small'
                              color='primary'
                              disabled={actionLoading === user.id}
                              onClick={() => handleFeeSave(user.id)}
                            >
                              <CheckIcon fontSize='small' />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {user.platformFeeOverride !== null && (
                          <Tooltip title='Clear override (revert to default 5%)'>
                            <span>
                              <IconButton
                                size='small'
                                color='default'
                                disabled={actionLoading === user.id}
                                onClick={() => handleFeeClear(user.id)}
                              >
                                <ClearIcon fontSize='small' />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{user.createdAt}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {user.accountStatus === 'active' && (
                          <Button
                            size='small'
                            variant='outlined'
                            color='warning'
                            disabled={actionLoading === user.id}
                            onClick={() => handleSuspend(user.id)}
                          >
                            Suspend
                          </Button>
                        )}
                        {(user.accountStatus === 'active' ||
                          user.accountStatus === 'suspended') && (
                          <Button
                            size='small'
                            variant='outlined'
                            color='error'
                            disabled={actionLoading === user.id}
                            onClick={() => handleBan(user.id)}
                          >
                            Ban
                          </Button>
                        )}
                        {user.accountStatus === 'suspended' && (
                          <Button
                            size='small'
                            variant='outlined'
                            color='success'
                            disabled={actionLoading === user.id}
                            onClick={() => handleUnsuspend(user.id)}
                          >
                            Unsuspend
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* User Detail: Acknowledgement History */}
      {selectedUserId && (
        <Box sx={{ mt: 3 }}>
          <Typography variant='h6' gutterBottom>
            Acknowledgement History
          </Typography>
          <AcknowledgementHistory userId={selectedUserId} />
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant='filled'
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default UserManagementTab
